import { useEffect, useRef, useState } from "react";

import { services } from "@src/dependencies";

export const useTrekSettings = ({ organizationCode, enabled }) => {
  const previousSettings = useRef();
  const { trekService } = services;
  const [settings, setSettings] = useState();
  const [fetchingSettings, setFetchingSettings] = useState(false);
  const [errorFetchingSettings, setErrorFetchingSettings] = useState();
  const [applyingChanges, setApplyingChanges] = useState(false);
  const [errorApplyingChanges, setErrorApplyingChanges] = useState();
  const [successApplyingChanges, setSuccessApplyingChanges] = useState(false);

  const _fetchTrekSettings = async () => {
    _resetStatus({ includeSettings: true });
    setFetchingSettings(true);

    try {
      const settings =
        await trekService.getOrganizationSettings(organizationCode);
      previousSettings.current = settings;

      setSettings((p) => ({
        ...p,
        ...settings,
      }));
    } catch (error) {
      setErrorFetchingSettings(error);
    } finally {
      setFetchingSettings(false);
    }
  };

  const _resetStatus = (options) => {
    const { includeSettings = false, includeSuccess = false } = options || {};

    setFetchingSettings();
    setErrorFetchingSettings();
    setApplyingChanges();
    setErrorApplyingChanges();

    if (includeSuccess) {
      setSuccessApplyingChanges();
    }

    if (includeSettings) {
      setSettings();
    }
  };

  const applyChanges = async () => {
    _resetStatus({ includeSuccess: true });
    setApplyingChanges(true);

    try {
      const { msa, advisorRole } = settings;
      const payload = {};

      if (msa?.code !== previousSettings.current.msa?.code) {
        // Send explicit null to remove the msa if it's meant to be removed
        // eslint-disable-next-line unicorn/no-null
        payload.msa = msa ?? null;
      }

      if (advisorRole !== previousSettings.current.advisorRole) {
        payload.advisorRole = advisorRole;
      }

      await trekService.updateOrganizationSettings(organizationCode, payload);

      setSuccessApplyingChanges(true);
      _fetchTrekSettings();
    } catch (error) {
      setErrorApplyingChanges(error);
    } finally {
      setApplyingChanges(false);
    }
  };

  const update = (key, value) => {
    setSettings((p) => ({ ...p, [key]: value }));
  };

  useEffect(() => {
    if (enabled && organizationCode) {
      _fetchTrekSettings();
    }
  }, [enabled, organizationCode]);

  const settingsChanged =
    !!settings &&
    !!previousSettings.current &&
    Object.keys(previousSettings.current).some(
      (key) =>
        JSON.stringify(settings[key]) !==
        JSON.stringify(previousSettings.current[key]),
    );

  return {
    settings,
    update,
    applyChanges,
    fetchingSettings,
    applyingChanges,
    errorFetchingSettings,
    errorApplyingChanges,
    successApplyingChanges,
    settingsChanged,
  };
};
